import { useEffect, useRef, useState } from 'react';
import type { flowIdeas } from '@repletoryreach/sdk-api';
import { Button, Group, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { Check } from '@repletoryreach/sdk-ui/icons';
import { ReplyHtml } from '../../../app/assistant/ReplyHtml';
import { useI18n } from '../../../i18n/I18nProvider';
import { formatCredits } from '../../../lib/credits';
import { formatCount } from '../../../lib/format';
import { formatLocalDate } from '../../bookings/lib/time';
import { activityText, hasOpenQuestions, memoryPct } from './lib/data';

const MUTED = 'var(--rr-color-text-muted)';
/** A person's message longer than this shows its first part, with Show all: a pasted document should not fill the chat. */
const LONG_MESSAGE = 600;

/** The person's own words, shortened when long, with a toggle to read them all. */
function PersonText({ text }: { text: string }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const long = text.length > LONG_MESSAGE;
  return (
    <>
      <Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{long && !open ? `${text.slice(0, LONG_MESSAGE).trimEnd()}…` : text}</Text>
      {long && (
        <button type="button" className="rr-ideas__more" onClick={() => setOpen((v) => !v)} data-testid="ideas-msg-more">
          {open ? t('flowIdeas.chat.showLess') : t('flowIdeas.chat.showAll', { count: formatCount(text.length) })}
        </button>
      )}
    </>
  );
}

export interface Composer {
  draft: string;
  setDraft: (text: string) => void;
  estimate: flowIdeas.FlowIdeaEstimate | null;
  sending: boolean;
  error: string | null;
}

/**
 * The AI's side of the page (owner, 30 October 2026: "ai right side will show when its started to think and doing
 * something"): before anything starts, a note; then what it is doing now, line by line; its replies and its questions,
 * each with buttons to answer; how full its memory is; and the composer, with what the next message costs.
 */
export function AiPanel({ set, composer, onSend, onFresh, freshing, onFocusRef }: {
  set: flowIdeas.FlowIdeaSet | null;
  composer: Composer;
  onSend: (text: string) => void;
  onFresh: () => void;
  freshing: boolean;
  /** Lets the page put the cursor in the composer ("Ask the AI to change this"). */
  onFocusRef?: (focus: () => void) => void;
}) {
  const { t, locale } = useI18n();
  const logRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [showNote, setShowNote] = useState(false);
  const thinking = set?.status === 'thinking';
  const busy = thinking || set?.status === 'building';
  const messages = set?.messages ?? [];
  const open = hasOpenQuestions(messages);
  const memory = set?.memory;
  const date = (iso: string) => formatLocalDate(iso.slice(0, 10), locale.includes('-') ? locale : `${locale}-ZA`, { day: 'numeric', month: 'short' });

  useEffect(() => { onFocusRef?.(() => inputRef.current?.focus()); }, [onFocusRef]);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, thinking]);

  const send = (text: string) => {
    if (!text.trim() || busy || composer.sending) return;
    onSend(text.trim());
  };
  const est = composer.estimate;

  return (
    <>
      <div className="rr-ideas__ai-head">
        <Title order={4}>{t('flowIdeas.ai.title')}</Title>
        {set && set.spent_minor > 0 && (
          <Text role="caption" c={MUTED} data-testid="ideas-spent">
            {t('flowIdeas.ai.spent', { credits: formatCredits(set.spent_minor, t), tokens: formatCount(set.tokens_in + set.tokens_out) })}
          </Text>
        )}
      </div>

      {memory && (
        <div className={`rr-ideas__memory is-${memory.band === 'green' ? 'green' : memory.band}`} data-testid="ideas-memory" data-band={memory.band}>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text role="caption" fw={600}>{t('flowIdeas.memory.label')}</Text>
            <Text role="caption" c={MUTED}>{t('flowIdeas.memory.used', { used: formatCount(memory.used_tokens), budget: formatCount(memory.budget_tokens) })}</Text>
          </Group>
          <div className="rr-ideas__memory-bar" role="meter" aria-valuemin={0} aria-valuemax={memory.budget_tokens} aria-valuenow={memory.used_tokens}
            aria-label={t('flowIdeas.memory.label')}>
            <div className="rr-ideas__memory-fill" style={{ width: `${memoryPct(memory)}%` }} />
          </div>
          <Text role="caption" c={MUTED}>{t(`flowIdeas.memory.${memory.band}`)}</Text>
          {memory.folded && (
            <Stack gap={4}>
              <Group gap="xs" wrap="wrap">
                <Text role="caption" data-testid="ideas-folded">{t('flowIdeas.memory.folded')}</Text>
                {memory.summary && (
                  <Button size="xs" variant="subtle" onClick={() => setShowNote((v) => !v)} data-testid="ideas-folded-note">
                    {showNote ? t('flowIdeas.memory.hideNote') : t('flowIdeas.memory.showNote')}
                  </Button>
                )}
              </Group>
              {showNote && memory.summary && <Text role="caption" style={{ whiteSpace: 'pre-wrap' }}>{memory.summary}</Text>}
            </Stack>
          )}
        </div>
      )}

      {set && set.activity.length > 0 && (busy || set.activity.some((a) => a.state !== 'done')) && (
        <ol className="rr-ideas__activity" aria-label={t('flowIdeas.activity.title')} aria-live="polite" data-testid="ideas-activity">
          {set.activity.map((a, i) => (
            <li key={`${a.key}-${i}`} className={`is-${a.state}`} data-state={a.state}>
              <span className="rr-ideas__dot" aria-hidden>{a.state === 'done' ? <Check size={11} /> : ''}</span>
              <span className="rr-ideas__activity-text">{activityText(t, a, date)}</span>
            </li>
          ))}
        </ol>
      )}

      <div className="rr-ideas__log" ref={logRef} aria-live="polite" data-testid="ideas-log">
        {!set && (
          <div className="rr-ideas__msg rr-ideas__msg--system" data-testid="ideas-ai-idle">
            <Text role="bodySmall">{t('flowIdeas.ai.idle')}</Text>
          </div>
        )}
        {messages.map((m, idx) => {
          const last = idx === messages.length - 1;
          return (
            <div key={m.n} className={`rr-ideas__msg rr-ideas__msg--${m.role}`} data-testid={`ideas-msg-${m.role}`}>
              {m.role === 'assistant'
                ? <div className="rr-ideas__reply"><ReplyHtml html={m.text} /></div>
                : m.role === 'user' ? <PersonText text={m.text} /> : <Text role="bodySmall" style={{ whiteSpace: 'pre-wrap' }}>{m.text}</Text>}
              {m.questions.map((q, qi) => (
                <div key={qi} className="rr-ideas__question" data-testid="ideas-question">
                  <Text role="bodySmall" fw={600}>{q.text}</Text>
                  {q.options.length > 0 && (
                    <div className="rr-ideas__chips">
                      {q.options.map((o) => (
                        <button key={o} type="button" className="rr-ideas__chip" disabled={!last || busy || composer.sending}
                          onClick={() => send(o)} data-testid="ideas-answer">{o}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {m.role === 'assistant' && (m.tokens_in != null || m.spent_minor != null) && (
                <Text role="caption" c={MUTED} style={{ marginTop: 6 }} data-testid="ideas-msg-cost">
                  {t('flowIdeas.ai.used', {
                    tokens: formatCount((m.tokens_in ?? 0) + (m.tokens_out ?? 0)),
                    credits: m.spent_minor == null ? '-' : formatCredits(m.spent_minor, t)
                  })}
                </Text>
              )}
            </div>
          );
        })}
        {thinking && (
          <div className="rr-ideas__msg rr-ideas__msg--assistant" data-testid="ideas-thinking">
            <Text role="caption" c={MUTED}>{t('flowIdeas.ai.thinking')}</Text>
          </div>
        )}
      </div>

      {memory?.too_big ? (
        <Alert color="warning" data-testid="ideas-too-big">
          <Stack gap="xs">
            <Text role="bodySmall">{t('flowIdeas.memory.tooBig')}</Text>
            <div><Button size="xs" onClick={onFresh} loading={freshing} data-testid="ideas-fresh">{t('flowIdeas.memory.fresh')}</Button></div>
          </Stack>
        </Alert>
      ) : set && (
        <form className="rr-ideas__composer" onSubmit={(e) => { e.preventDefault(); send(composer.draft); }}>
          <Textarea
            ref={inputRef}
            aria-label={t('flowIdeas.ai.placeholder')}
            placeholder={open ? t('flowIdeas.ai.placeholderAnswer') : t('flowIdeas.ai.placeholder')}
            value={composer.draft}
            minRows={2}
            maxRows={6}
            disabled={composer.sending}
            onChange={(e) => composer.setDraft(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(composer.draft); } }}
            data-testid="ideas-composer"
          />
          <Text role="caption" c={MUTED} data-testid="ideas-estimate">
            {est ? t('flowIdeas.ai.estimate', {
              in: formatCount(est.tokens_in), out: formatCount(est.tokens_out),
              credits: est.minor == null ? '-' : formatCredits(est.minor, t)
            }) : t('flowIdeas.ai.estimateNone')}
          </Text>
          {est && !est.fits && <Text role="caption" c="var(--rr-color-error)">{t('flowIdeas.ai.wontFit')}</Text>}
          {est && est.fits && !est.affordable && <Text role="caption" c="var(--rr-color-error)">{t('flowIdeas.error.credits')}</Text>}
          {composer.error && <Alert color="error" role="alert">{composer.error}</Alert>}
          <Group justify="space-between" gap="xs">
            <Text role="caption" c={MUTED}>{busy ? t('flowIdeas.ai.wait') : ''}</Text>
            <Button size="sm" type="submit" disabled={!composer.draft.trim() || busy} loading={composer.sending} data-testid="ideas-send">
              {t('flowIdeas.ai.send')}
            </Button>
          </Group>
        </form>
      )}
    </>
  );
}
